import type { ReactNode, Ref } from 'react'
import cx from 'classnames'
import styles from './Chip.module.css'
import { Icon } from '@/components/Icon'
import { RawButton } from '@/components/Button'
import { useControlSize } from '@/components/inputs/control-size'
import { match } from '@/utils/pattern-match-util'

interface ChipProps {
  children?: ReactNode
  onRemove?: () => unknown
  icon?: ReactNode
  variant?: 'default' | 'filter'
  onPress?: () => void
  ref?: Ref<HTMLDivElement>
  testId?: string
}

/**
 * A compact label, optionally clickable and removable. Sized by the
 * surrounding `ControlSizeContext`, like the inputs it shows in.
 */
export function Chip({
  children,
  onRemove,
  icon,
  onPress,
  variant = 'default',
  testId,
  ref,
}: ChipProps) {
  // All variants but "filter" has the remove button be embedded within the main
  // area of the chip.
  const embedded = variant !== 'filter'
  const size = useControlSize()
  return (
    <div
      className={cx(
        styles.root,
        match(size, {
          normal: undefined,
          compact: styles.compact,
          small: styles.small,
        }),
      )}
      data-test={testId}
      ref={ref}
    >
      <div
        className={cx(
          styles.main,
          onPress && styles.clickable,
          embedded && styles.embedded,
        )}
      >
        {icon && <div className={styles.icon}>{icon}</div>}
        <div className={styles.content}>
          {onPress ? (
            <RawButton className={styles.mainButton} onPress={onPress}>
              <span className={styles.text}>{children}</span>
            </RawButton>
          ) : (
            <span className={styles.text}>{children}</span>
          )}
        </div>
        {onRemove && embedded && (
          <div className={styles.embeddedActions}>
            <RawButton
              className={styles.embeddedRemove}
              onPress={onRemove}
              testId={`${testId}-remove`}
            >
              <Icon size={16} name="close" />
            </RawButton>
          </div>
        )}
      </div>
      {onRemove && !embedded && (
        <RawButton
          className={styles.separatedRemove}
          onPress={onRemove}
          testId={`${testId}-remove`}
        >
          <Icon size={16} name="close" />
        </RawButton>
      )}
    </div>
  )
}
